import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Package, Plus, Edit2, Trash2, Save, X, ChevronDown, ChevronUp,
  Settings, Image, Tag, AlertCircle, ShoppingBag, Folder, Calendar as CalendarIcon,
  Ticket, BarChart3, Download, Clock, MapPin, FileText, Percent,
  Euro, Eye, EyeOff, CheckCircle, XCircle, Loader2, Upload, ImagePlus,
  Phone, Mail, User, Check, GripVertical, KeyRound, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import OrdersCalendar from '../components/OrdersCalendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ALLERGENS = ['gluten', 'eggs', 'dairy', 'nuts', 'soy'];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);
  const carouselFileInputRefs = useRef({});
  
  // Data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCarouselIndex, setUploadingCarouselIndex] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [ordersView, setOrdersView] = useState('list'); // 'list' | 'calendar'
  const [messages, setMessages] = useState([]);
  const unreadMessageCount = messages.filter((m) => !m.read).length;
  
  // Dialog states
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isCouponDialogOpen, setIsCouponDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [syncForm, setSyncForm] = useState({
    production_url: 'https://cake-craft-studio-3.emergent.host',
    admin_password: '',
  });
  const [syncing, setSyncing] = useState(false);

  // Change password dialog state
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const dragProductIndex = useRef(null);
  
  // Edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [isNewCoupon, setIsNewCoupon] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '', description: '', category: 'cakes', image: '', images: [], base_price: 0,
    sizes: [], flavors: [], allergens: [], customizable: true,
    allow_gift_note: true, gift_note_max_length: 100,
    allow_cake_message: true, cake_message_max_length: 50,
    allow_special_request: true, special_request_max_length: 250,
    stock_quantity: null,
    custom_text_template: { enabled: false, label: 'Choose your age', template: '{value} years', mode: 'list', options: ['18','21','25','30','40','50'], min: 1, max: 99 },
    active: true,
  });
  
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', image_url: '', active: true });
  
  const [couponForm, setCouponForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    min_order: 0, max_uses: null, expires_at: '', active: true, first_purchase_only: false
  });
  
  const [settingsForm, setSettingsForm] = useState({});

  // Blackout calendar
  const [selectedBlackoutDate, setSelectedBlackoutDate] = useState(null);
  const [blackoutReason, setBlackoutReason] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAllData();
  }, [isAuthenticated, token, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [productsRes, categoriesRes, ordersRes, couponsRes, blackoutRes, settingsRes, statsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/categories`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/coupons`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/blackout-dates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/dashboard/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/contact-messages`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setOrders(ordersRes.data);
      setCoupons(couponsRes.data);
      setBlackoutDates(blackoutRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
      setStats(statsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores.');
        navigate('/');
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================
  // MESSAGES (contact form)
  // ==================
  const fetchMessages = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const r = await axios.get(`${API}/admin/contact-messages`, { headers });
      setMessages(r.data);
    } catch {
      toast.error('Erro ao carregar mensagens');
    }
  };

  const handleToggleMessageRead = async (msg) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/contact-messages/${msg.id}?read=${!msg.read}`, null, { headers });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: !msg.read } : m)));
    } catch {
      toast.error('Erro ao atualizar mensagem');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/contact-messages/${id}`, { headers });
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success('Mensagem excluída');
    } catch {
      toast.error('Erro ao excluir mensagem');
    }
  };

  // ==================
  // PRODUCT FUNCTIONS
  // ==================
  const handleNewProduct = () => {
    setFormData({
      name: '', description: '', category: categories[0]?.id || 'cakes', image: '', images: [],
      base_price: 0, sizes: [], flavors: [], allergens: [], customizable: true,
      allow_gift_note: true, gift_note_max_length: 100,
      allow_cake_message: true, cake_message_max_length: 50,
      allow_special_request: true, special_request_max_length: 250,
      stock_quantity: null,
      custom_text_template: { enabled: false, label: 'Choose your age', template: '{value} years', mode: 'list', options: ['18','21','25','30','40','50'], min: 1, max: 99 },
      active: true,
    });
    setEditingProduct(null);
    setIsNewProduct(true);
    setIsProductDialogOpen(true);
  };

  const handleEditProduct = (product) => {    const defaultTpl = { enabled: false, label: 'Choose your age', template: '{value} years', mode: 'list', options: ['18','21','25','30','40','50'], min: 1, max: 99 };
    setFormData({
      name: product.name || '', description: product.description || '',
      category: product.category || 'cakes', image: product.image || '',
      images: product.images || [],
      base_price: product.base_price || 0, sizes: product.sizes || [],
      flavors: product.flavors || [], allergens: product.allergens || [],
      customizable: product.customizable ?? true,
      allow_gift_note: product.allow_gift_note ?? true,
      gift_note_max_length: product.gift_note_max_length ?? 100,
      allow_cake_message: product.allow_cake_message ?? true,
      cake_message_max_length: product.cake_message_max_length ?? 50,
      allow_special_request: product.allow_special_request ?? true,
      special_request_max_length: product.special_request_max_length ?? 250,
      stock_quantity: product.stock_quantity ?? null,
      custom_text_template: { ...defaultTpl, ...(product.custom_text_template || {}) },
      active: product.active ?? true,
    });
    setEditingProduct(product);
    setIsNewProduct(false);
    setIsProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Include main image in images array if not already there
      const productData = { ...formData };
      if (productData.image && !productData.images.includes(productData.image)) {
        productData.images = [productData.image, ...productData.images];
      }
      
      if (isNewProduct) {
        await axios.post(`${API}/admin/products`, productData, { headers });
        toast.success('Product created!');
      } else {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers });
        toast.success('Product updated!');
      }
      setIsProductDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleToggleProductActive = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { active: !product.active },
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success(product.active ? 'Produto desativado' : 'Produto ativado');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar produto');
    }
  };

  const handleDeleteProduct = async (product) => {
    const confirmed = window.confirm(
      `Tem a certeza que quer APAGAR "${product.name}"?\n\n` +
      `Esta acção é permanente e o produto deixa de aparecer no site imediatamente. ` +
      `Pedidos antigos com este produto continuam intactos.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/products/${product.id}`,
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success(`"${product.name}" apagado`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao apagar produto');
    }
  };

  // Image upload for products
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB.');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = response.data.url; // store relative URL so it works across envs
        setFormData({ ...formData, image: imageUrl });
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error('Error uploading image');
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  // Image upload for additional product images
  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB.');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = response.data.url; // store relative URL so it works across envs
        setFormData({ ...formData, images: [...(formData.images || []), imageUrl] });
        toast.success('Additional image uploaded!');
      }
    } catch (error) {
      toast.error('Error uploading image');
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  // Remove additional image
  const removeAdditionalImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  // Image upload for carousel
  const handleCarouselImageUpload = async (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB.');
      return;
    }

    setUploadingCarouselIndex(index);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = response.data.url; // store relative URL so it works across envs
        updateHeroImage(index, 'url', imageUrl);
        toast.success('Carousel image uploaded!');
      }
    } catch (error) {
      toast.error('Error uploading image');
      console.error('Upload error:', error);
    } finally {
      setUploadingCarouselIndex(null);
    }
  };

  // Size/Flavor helpers with stock
  const addSize = () => {
    setFormData({
      ...formData,
      sizes: [...formData.sizes, { id: '', name: '', price_modifier: 0, in_stock: true }]
    });
  };

  const updateSize = (index, field, value) => {
    const newSizes = [...formData.sizes];
    if (field === 'price_modifier') {
      newSizes[index][field] = parseFloat(value) || 0;
    } else if (field === 'in_stock') {
      newSizes[index][field] = value;
    } else {
      newSizes[index][field] = value;
      if (field === 'name') {
        newSizes[index].id = value.toLowerCase().replace(/[^a-z0-9]/g, '-');
      }
    }
    setFormData({ ...formData, sizes: newSizes });
  };

  const removeSize = (index) => {
    setFormData({ ...formData, sizes: formData.sizes.filter((_, i) => i !== index) });
  };

  const addFlavor = () => {
    setFormData({
      ...formData,
      flavors: [...formData.flavors, { id: '', name: '', price_modifier: 0, in_stock: true }]
    });
  };

  const updateFlavor = (index, field, value) => {
    const newFlavors = [...formData.flavors];
    if (field === 'price_modifier') {
      newFlavors[index][field] = parseFloat(value) || 0;
    } else if (field === 'in_stock') {
      newFlavors[index][field] = value;
    } else {
      newFlavors[index][field] = value;
      if (field === 'name') {
        newFlavors[index].id = value.toLowerCase().replace(/[^a-z0-9]/g, '-');
      }
    }
    setFormData({ ...formData, flavors: newFlavors });
  };

  const removeFlavor = (index) => {
    setFormData({ ...formData, flavors: formData.flavors.filter((_, i) => i !== index) });
  };

  const toggleAllergen = (allergen) => {
    const newAllergens = formData.allergens.includes(allergen)
      ? formData.allergens.filter(a => a !== allergen)
      : [...formData.allergens, allergen];
    setFormData({ ...formData, allergens: newAllergens });
  };

  // ==================
  // CATEGORY FUNCTIONS
  // ==================
  const handleNewCategory = () => {
    setCategoryForm({ name: '', description: '', image_url: '', active: true });
    setEditingCategory(null);
    setIsNewCategory(true);
    setIsCategoryDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name || '', description: category.description || '',
      image_url: category.image_url || '',
      active: category.active ?? true,
    });
    setEditingCategory(category);
    setIsNewCategory(false);
    setIsCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (isNewCategory) {
        await axios.post(`${API}/admin/categories`, categoryForm, { headers });
        toast.success('Categoria criada!');
      } else {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, categoryForm, { headers });
        toast.success('Categoria atualizada!');
      }
      setIsCategoryDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      await axios.put(`${API}/admin/categories/${category.id}`, { active: !category.active },
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success(category.active ? 'Categoria desativada' : 'Categoria ativada');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar categoria');
    }
  };

  // Drag-and-drop reordering
  const dragCategoryIndex = useRef(null);

  const handleCategoryDragStart = (idx) => {
    dragCategoryIndex.current = idx;
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
  };

  const handleCategoryDrop = async (dropIdx) => {
    const fromIdx = dragCategoryIndex.current;
    dragCategoryIndex.current = null;
    if (fromIdx === null || fromIdx === dropIdx) return;

    const reordered = [...categories];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setCategories(reordered);

    try {
      await axios.post(
        `${API}/admin/categories/reorder`,
        { order: reordered.map((c) => c.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordem atualizada!');
    } catch (error) {
      toast.error('Erro ao reordenar categorias');
      fetchAllData();
    }
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 5MB.');
      return;
    }
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        const imageUrl = response.data.url; // store relative URL so it works across envs
        setCategoryForm({ ...categoryForm, image_url: imageUrl });
        toast.success('Imagem de capa carregada!');
      }
    } catch (error) {
      toast.error('Erro ao carregar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  // Drag-and-drop reordering for products
  const handleProductDragStart = (idx) => {
    dragProductIndex.current = idx;
  };

  const handleProductDragOver = (e) => {
    e.preventDefault();
  };

  const handleProductDrop = async (dropIdx, visibleProducts) => {
    const fromIdx = dragProductIndex.current;
    dragProductIndex.current = null;
    if (fromIdx === null || fromIdx === dropIdx) return;

    // Build new full products list reflecting the swap of two items in the visible (filtered) list
    const reorderedVisible = [...visibleProducts];
    const [moved] = reorderedVisible.splice(fromIdx, 1);
    reorderedVisible.splice(dropIdx, 0, moved);

    // Merge back into full products list keeping non-visible products' relative order
    const visibleIds = new Set(visibleProducts.map((p) => p.id));
    const merged = [];
    let visibleCursor = 0;
    for (const p of products) {
      if (visibleIds.has(p.id)) {
        merged.push(reorderedVisible[visibleCursor++]);
      } else {
        merged.push(p);
      }
    }
    setProducts(merged);

    try {
      await axios.post(
        `${API}/admin/products/reorder`,
        { order: merged.map((p) => p.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordem dos produtos atualizada!');
    } catch (error) {
      toast.error('Erro ao reordenar produtos');
      fetchAllData();
    }
  };

  const handleSyncToProduction = async () => {
    if (!syncForm.admin_password) {
      toast.error('Digite a senha de admin de produção');
      return;
    }
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/admin/sync-to-production`,
        {
          production_url: syncForm.production_url,
          admin_email: 'madbrothersdublin@gmail.com',
          admin_password: syncForm.admin_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { imported } = response.data;
      toast.success(
        `Sincronização concluída! ${imported.products} produtos, ${imported.categories} categorias, ${imported.coupons} cupons.`,
        { duration: 6000 }
      );
      setIsSyncDialogOpen(false);
      setSyncForm({ ...syncForm, admin_password: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar com produção');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncFromProduction = async () => {
    if (!syncForm.admin_password) {
      toast.error('Digite a senha de admin de produção');
      return;
    }
    const confirm = window.confirm(
      'Isso vai APAGAR os dados atuais do preview e substituir pelos dados da produção. Tem certeza?'
    );
    if (!confirm) return;
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/admin/sync-from-production`,
        {
          production_url: syncForm.production_url,
          admin_email: 'madbrothersdublin@gmail.com',
          admin_password: syncForm.admin_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { imported } = response.data;
      toast.success(
        `Preview atualizado! ${imported.products} produtos, ${imported.categories} categorias, ${imported.coupons} cupons puxados da produção.`,
        { duration: 6000 }
      );
      setIsSyncDialogOpen(false);
      setSyncForm({ ...syncForm, admin_password: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao puxar da produção');
    } finally {
      setSyncing(false);
    }
  };

  // ==================
  // COUPON FUNCTIONS
  // ==================
  const handleNewCoupon = () => {
    setCouponForm({
      code: '', discount_type: 'percentage', discount_value: 10,
      min_order: 0, max_uses: null, expires_at: '', active: true, first_purchase_only: false
    });
    setEditingCoupon(null);
    setIsNewCoupon(true);
    setIsCouponDialogOpen(true);
  };

  const handleEditCoupon = (coupon) => {
    setCouponForm({
      code: coupon.code || '', discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value || 10, min_order: coupon.min_order || 0,
      max_uses: coupon.max_uses, expires_at: coupon.expires_at || '', active: coupon.active ?? true,
      first_purchase_only: coupon.first_purchase_only ?? false
    });
    setEditingCoupon(coupon);
    setIsNewCoupon(false);
    setIsCouponDialogOpen(true);
  };

  const handleSaveCoupon = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (isNewCoupon) {
        await axios.post(`${API}/admin/coupons`, couponForm, { headers });
        toast.success('Cupão criado!');
      } else {
        await axios.put(`${API}/admin/coupons/${editingCoupon.id}`, couponForm, { headers });
        toast.success('Cupão atualizado!');
      }
      setIsCouponDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cupão');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este cupão?')) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`,
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Cupão eliminado');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao eliminar cupão');
    }
  };

  // ==================
  // BLACKOUT DATES
  // ==================
  const handleAddBlackoutDate = async () => {
    if (!selectedBlackoutDate) return;
    const dateStr = selectedBlackoutDate.toISOString().split('T')[0];
    try {
      await axios.post(`${API}/admin/blackout-dates`, 
        { date: dateStr, reason: blackoutReason },
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Data bloqueada!');
      setSelectedBlackoutDate(null);
      setBlackoutReason('');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao bloquear data');
    }
  };

  const handleRemoveBlackoutDate = async (date) => {
    try {
      await axios.delete(`${API}/admin/blackout-dates/${date}`,
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Data desbloqueada!');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao desbloquear data');
    }
  };

  // ==================
  // SETTINGS
  // ==================
  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settingsForm,
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Configurações salvas!');
      setIsSettingsDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    }
  };

  // ==================
  // CHANGE PASSWORD
  // ==================
  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordForm;
    if (!current_password || !new_password || !confirm_password) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (new_password.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    if (new_password !== confirm_password) {
      toast.error('A nova senha e a confirmação não coincidem');
      return;
    }
    if (new_password === current_password) {
      toast.error('A nova senha deve ser diferente da atual');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password, new_password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Senha atualizada com sucesso!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setIsPasswordDialogOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  // ==================
  // TEST EMAIL (diagnostic)
  // ==================
  const handleWarmImageCache = async () => {
    try {
      toast.info('Pre-baixando imagens em background...');
      const res = await axios.post(`${API}/admin/warm-image-cache`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      toast.success(
        `Cache aquecido! ${d.warmed} novas, ${d.already_cached} já em cache, ${d.failed} falharam (total ${d.total})`,
        { duration: 8000 }
      );
    } catch (e) {
      toast.error('Falha ao aquecer cache: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleTestEmail = async () => {
    const toAddr = window.prompt(
      'Para qual email enviar o teste?\n(Deixe vazio para usar o email de notificação configurado)',
      ''
    );
    if (toAddr === null) return; // cancelled
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const cfg = await axios.get(`${API}/admin/email-config`, { headers });
      const res = await axios.post(`${API}/admin/test-email`, { to: toAddr || undefined }, { headers });
      const d = res.data;
      const cd = cfg.data;
      const msg = (
        `Sender: ${cd.sender_from_effective}\n` +
        `RESEND key set: ${cd.resend_api_key_set ? 'YES (' + cd.resend_api_key_preview + ')' : 'NO'}\n` +
        `SENDER_EMAIL env: ${cd.sender_email_env}\n\n` +
        (d.success
          ? `✅ EMAIL ENVIADO!\nPara: ${d.to}\nResponse: ${d.resend_response}`
          : `❌ FALHOU\nErro: ${d.error}\nTipo: ${d.error_type || '-'}`)
      );
      // Use a longer-lived dialog by alerting (so the user can copy the error if needed)
      window.alert(msg);
      if (d.success) toast.success(`Email de teste enviado para ${d.to}`);
      else toast.error('Falha ao enviar — veja o detalhe no popup');
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao testar email');
    }
  };

  // Hero image helpers
  const addHeroImage = () => {
    const newImages = [...(settingsForm.hero_images || []), 
      { id: Date.now().toString(), url: '', title: '', subtitle: '', active: true }];
    setSettingsForm({ ...settingsForm, hero_images: newImages });
  };

  const updateHeroImage = (index, field, value) => {
    const newImages = [...(settingsForm.hero_images || [])];
    newImages[index][field] = value;
    setSettingsForm({ ...settingsForm, hero_images: newImages });
  };

  const removeHeroImage = (index) => {
    const newImages = settingsForm.hero_images.filter((_, i) => i !== index);
    setSettingsForm({ ...settingsForm, hero_images: newImages });
  };

  // Pickup slot helpers
  const addPickupSlot = () => {
    const newSlots = [...(settingsForm.pickup_slots || []), 
      { id: Date.now().toString(), name: '', start: '09:00', end: '12:00', active: true }];
    setSettingsForm({ ...settingsForm, pickup_slots: newSlots });
  };

  const updatePickupSlot = (index, field, value) => {
    const newSlots = [...(settingsForm.pickup_slots || [])];
    newSlots[index][field] = value;
    setSettingsForm({ ...settingsForm, pickup_slots: newSlots });
  };

  const removePickupSlot = (index) => {
    const newSlots = settingsForm.pickup_slots.filter((_, i) => i !== index);
    setSettingsForm({ ...settingsForm, pickup_slots: newSlots });
  };

  // ==================
  // ORDERS
  // ==================
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {},
        { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Status atualizado!');
      fetchAllData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleExportOrders = async (date = null) => {
    try {
      const params = date ? `?date=${date}` : '';
      window.open(`${API}/admin/orders/export/csv${params}`, '_blank');
      toast.success('Exportação iniciada!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#8D6E63] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] overflow-x-hidden" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1a1a1a]">
              Painel Admin
            </h1>
            <p className="text-stone-500 mt-1">Controle total da sua loja</p>
          </div>
          <Button onClick={() => { setSettingsForm(settings); setIsSettingsDialogOpen(true); }}
            variant="outline" className="rounded-full">
            <Settings className="w-4 h-4 mr-2" /> Configurações
          </Button>
          <Button
            onClick={() => { setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); setIsPasswordDialogOpen(true); }}
            variant="outline"
            className="rounded-full"
            data-testid="change-password-btn"
          >
            <KeyRound className="w-4 h-4 mr-2" /> Alterar Senha
          </Button>
          <Button
            onClick={handleTestEmail}
            variant="outline"
            className="rounded-full"
            data-testid="test-email-btn"
          >
            <Send className="w-4 h-4 mr-2" /> Testar Email
          </Button>
          <Button
            onClick={handleWarmImageCache}
            variant="outline"
            className="rounded-full"
            data-testid="warm-cache-btn"
          >
            <Image className="w-4 h-4 mr-2" /> Aquecer Imagens
          </Button>
          {typeof window !== 'undefined' && window.location.hostname.includes('.preview.') && (
            <Button
              onClick={() => setIsSyncDialogOpen(true)}
              className="rounded-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#1a1a1a]"
              data-testid="sync-prod-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Sincronizar com Produção
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="w-4 h-4" /> Produtos ({products.length})
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Folder className="w-4 h-4" /> Categorias
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Pedidos ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" /> Calendário
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center gap-2">
              <Ticket className="w-4 h-4" /> Cupões
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="tab-messages">
              <Mail className="w-4 h-4" /> Mensagens{unreadMessageCount > 0 ? ` (${unreadMessageCount})` : ''}
            </TabsTrigger>
          </TabsList>

          {/* ==================== DASHBOARD ==================== */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1a1a]">{stats.total_orders || 0}</p>
                    <p className="text-xs text-stone-500">Total Pedidos</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Euro className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1a1a]">€{stats.total_revenue || 0}</p>
                    <p className="text-xs text-stone-500">Receita Total</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1a1a]">{stats.pending_orders || 0}</p>
                    <p className="text-xs text-stone-500">Pendentes</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1a1a]">{stats.products_count || 0}</p>
                    <p className="text-xs text-stone-500">Produtos Ativos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Orders Quick View */}
            <div className="bg-white rounded-xl shadow-sm border border-stone-100 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-semibold text-lg text-[#1a1a1a]">Pedidos Recentes</h2>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('orders')}>
                  Ver Todos
                </Button>
              </div>
              <div className="space-y-3">
                {orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                    <div>
                      <p className="font-medium text-sm">{order.contact_name}</p>
                      <p className="text-xs text-stone-500">{order.pickup_date} às {order.pickup_time}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#8D6E63]">€{order.total?.toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        order.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        order.status === 'pending_payment' ? 'bg-amber-100 text-amber-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>{order.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* ==================== PRODUCTS ==================== */}
          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Label className="text-sm text-stone-600 whitespace-nowrap">Filtrar por categoria:</Label>
                <select
                  value={productCategoryFilter}
                  onChange={(e) => setProductCategoryFilter(e.target.value)}
                  className="h-9 text-sm border border-stone-300 rounded-md px-3 bg-white"
                  data-testid="admin-products-category-filter"
                >
                  <option value="all">Todas as categorias ({products.length})</option>
                  {categories.map((c) => {
                    const count = products.filter((p) => p.category === c.id).length;
                    return <option key={c.id} value={c.id}>{c.name} ({count})</option>;
                  })}
                </select>
              </div>
              <Button onClick={handleNewProduct} className="bg-[#8D6E63] hover:bg-[#8D6E63]/90 rounded-full"
                data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" /> Novo Produto
              </Button>
            </div>

            <p className="text-xs text-stone-500 mb-3 px-1 flex items-center gap-1.5">
              <GripVertical className="w-3 h-3" />
              Arraste pelo ícone para reordenar. A ordem aqui define a ordem na página /shop.
            </p>

            <div className="grid gap-4">
              {(() => {
                const visibleProducts = productCategoryFilter === 'all'
                  ? products
                  : products.filter((p) => p.category === productCategoryFilter);
                return visibleProducts.map((product, idx) => (
                <motion.div key={product.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  draggable
                  onDragStart={() => handleProductDragStart(idx)}
                  onDragOver={handleProductDragOver}
                  onDrop={() => handleProductDrop(idx, visibleProducts)}
                  className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-shadow hover:shadow-md ${product.active ? 'border-stone-100' : 'border-red-200 bg-red-50/50'}`}
                  data-testid={`admin-product-row-${product.id}`}>
                  <div className="p-4 flex items-center gap-4 min-w-0">
                    <button
                      type="button"
                      className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-[#8D6E63] p-1 -ml-1 flex-shrink-0"
                      aria-label="Arrastar para reordenar"
                      data-testid={`admin-product-drag-${product.id}`}
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-semibold text-[#1a1a1a] truncate min-w-0">{product.name}</h3>
                        {!product.active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded flex-shrink-0">Inativo</span>}
                        {product.stock_quantity === 0 && (
                          <span className="text-xs bg-red-600 text-white font-bold px-2 py-0.5 rounded uppercase tracking-wider flex-shrink-0"
                            data-testid={`admin-product-soldout-${product.id}`}>
                            Esgotado
                          </span>
                        )}
                        {product.stock_quantity > 0 && product.stock_quantity <= 3 && (
                          <span className="text-xs bg-amber-500 text-white font-bold px-2 py-0.5 rounded flex-shrink-0">
                            {product.stock_quantity} restantes
                          </span>
                        )}
                      </div>
                      <p className="text-stone-500 text-sm truncate">{product.description}</p>
                      <div className="flex items-center gap-4 mt-1 min-w-0 overflow-hidden">
                        <span className="text-[#8D6E63] font-bold flex-shrink-0">€{product.base_price?.toFixed(2)}</span>
                        <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded flex-shrink-0 truncate max-w-[120px]">
                          {categories.find(c => c.id === product.category)?.name || product.category}
                        </span>
                        {product.sizes?.length > 0 && <span className="text-xs text-blue-600 flex-shrink-0">{product.sizes.length} tamanhos</span>}
                        {product.flavors?.length > 0 && <span className="text-xs text-purple-600 flex-shrink-0">{product.flavors.length} sabores</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Switch checked={product.active} onCheckedChange={() => handleToggleProductActive(product)} />
                      <Button variant="outline" size="icon" onClick={() => handleEditProduct(product)} className="rounded-full"
                        data-testid={`edit-product-${product.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteProduct(product)}
                        className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                        data-testid={`delete-product-${product.id}`}
                        aria-label={`Apagar ${product.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)} className="rounded-full">
                        {expandedProduct === product.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <AnimatePresence>
                    {expandedProduct === product.id && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        className="px-4 pb-4 border-t border-stone-100 mt-2 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          {product.sizes?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-[#1a1a1a] mb-2">Tamanhos</h4>
                              <ul className="space-y-1">
                                {product.sizes.map((size) => (
                                  <li key={size.id} className="flex items-center justify-between text-stone-600">
                                    <span>{size.name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[#8D6E63]">+€{size.price_modifier}</span>
                                      {size.in_stock === false ? 
                                        <XCircle className="w-4 h-4 text-red-500" /> : 
                                        <CheckCircle className="w-4 h-4 text-green-500" />}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {product.flavors?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-[#1a1a1a] mb-2">Sabores</h4>
                              <ul className="space-y-1">
                                {product.flavors.map((flavor) => (
                                  <li key={flavor.id} className="flex items-center justify-between text-stone-600">
                                    <span>{flavor.name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[#8D6E63]">+€{flavor.price_modifier}</span>
                                      {flavor.in_stock === false ? 
                                        <XCircle className="w-4 h-4 text-red-500" /> : 
                                        <CheckCircle className="w-4 h-4 text-green-500" />}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {product.allergens?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-[#1a1a1a] mb-2">Alergénios</h4>
                              <div className="flex flex-wrap gap-1">
                                {product.allergens.map((a) => (
                                  <span key={a} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{a}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ));
              })()}
            </div>
          </TabsContent>

          {/* ==================== CATEGORIES ==================== */}
          <TabsContent value="categories">
            <div className="flex justify-end mb-4">
              <Button onClick={handleNewCategory} className="bg-[#8D6E63] hover:bg-[#8D6E63]/90 rounded-full"
                data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-2" /> Nova Categoria
              </Button>
            </div>
            
            <div className="grid gap-4">
              <p className="text-xs text-stone-500 -mb-2 px-1">
                Arraste pelo ícone <GripVertical className="w-3 h-3 inline -mt-0.5" /> para reordenar. A ordem aqui define a ordem na página /shop.
              </p>
              {categories.map((category, idx) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  draggable
                  onDragStart={() => handleCategoryDragStart(idx)}
                  onDragOver={handleCategoryDragOver}
                  onDrop={() => handleCategoryDrop(idx)}
                  className={`bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between transition-shadow hover:shadow-md ${
                    category.active ? 'border-stone-100' : 'border-red-200 bg-red-50/50'}`}
                  data-testid={`category-row-${category.id}`}>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-[#8D6E63] p-1 -ml-1"
                      aria-label="Arrastar para reordenar"
                      data-testid={`category-drag-${category.id}`}
                    >
                      <GripVertical className="w-5 h-5" />
                    </button>
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-12 h-12 rounded-xl object-cover border border-stone-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-[#8D6E63]/10 rounded-xl flex items-center justify-center">
                        <Folder className="w-6 h-6 text-[#8D6E63]" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-[#1a1a1a]">{category.name}</h3>
                        {!category.active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inativa</span>}
                      </div>
                      <p className="text-stone-500 text-sm">{category.description}</p>
                      <p className="text-xs text-stone-400 mt-1">ID: {category.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={category.active} onCheckedChange={() => handleToggleCategoryActive(category)} />
                    <Button variant="outline" size="icon" onClick={() => handleEditCategory(category)} className="rounded-full">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* ==================== ORDERS ==================== */}
          <TabsContent value="orders">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
              <div className="flex gap-2 items-center">
                {/* View mode toggle */}
                <div className="inline-flex bg-stone-100 rounded-full p-1" data-testid="orders-view-toggle">
                  <button
                    type="button"
                    onClick={() => setOrdersView('list')}
                    className={`px-4 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1.5 ${
                      ordersView === 'list' ? 'bg-white shadow text-[#1a1a1a]' : 'text-stone-500 hover:text-[#1a1a1a]'
                    }`}
                    data-testid="orders-view-list"
                  >
                    <FileText className="w-4 h-4" /> Lista
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrdersView('calendar')}
                    className={`px-4 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1.5 ${
                      ordersView === 'calendar' ? 'bg-white shadow text-[#1a1a1a]' : 'text-stone-500 hover:text-[#1a1a1a]'
                    }`}
                    data-testid="orders-view-calendar"
                  >
                    <CalendarIcon className="w-4 h-4" /> Calendário
                  </button>
                </div>
                {ordersView === 'list' && (
                  <Select defaultValue="all">
                    <SelectTrigger className="w-40"><SelectValue placeholder="Filtrar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="confirmed">Confirmados</SelectItem>
                      <SelectItem value="pending_payment">Pendentes</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Button onClick={() => handleExportOrders()} variant="outline" className="rounded-full">
                <Download className="w-4 h-4 mr-2" /> Exportar CSV
              </Button>
            </div>

            {ordersView === 'calendar' ? (
              <OrdersCalendar orders={orders} />
            ) : orders.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <ShoppingBag className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhum pedido ainda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden hover:shadow-md transition-shadow">
                    {/* Order Header */}
                    <div className="p-4 flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                      <div className="flex items-center gap-4">
                        {/* Status Icon */}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          order.status === 'confirmed' ? 'bg-green-100' :
                          order.status === 'preparing' ? 'bg-blue-100' :
                          order.status === 'ready' ? 'bg-purple-100' :
                          order.status === 'delivered' ? 'bg-emerald-100' :
                          order.status === 'cancelled' ? 'bg-red-100' :
                          'bg-amber-100'
                        }`}>
                          <ShoppingBag className={`w-6 h-6 ${
                            order.status === 'confirmed' ? 'text-green-600' :
                            order.status === 'preparing' ? 'text-blue-600' :
                            order.status === 'ready' ? 'text-purple-600' :
                            order.status === 'delivered' ? 'text-emerald-600' :
                            order.status === 'cancelled' ? 'text-red-600' :
                            'text-amber-600'
                          }`} />
                        </div>
                        
                        {/* Customer Info */}
                        <div>
                          <p className="font-semibold text-[#1a1a1a] text-lg">{order.contact_name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="inline-flex items-center gap-1 text-sm text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full">
                              <Phone className="w-3 h-3" />
                              {order.contact_phone}
                            </span>
                            {order.contact_email && (
                              <span className="inline-flex items-center gap-1 text-sm text-stone-600 bg-stone-100 px-2 py-0.5 rounded-full">
                                <Mail className="w-3 h-3" />
                                {order.contact_email}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-stone-500 mt-1 flex items-center gap-1">
                            <CalendarIcon className="w-3.5 h-3.5" />
                            🏪 Recolha: <span className="font-medium text-[#8D6E63]">{order.pickup_date}</span> às <span className="font-medium text-[#8D6E63]">{order.pickup_time}</span>
                          </p>
                        </div>
                      </div>
                      
                      {/* Price & Status */}
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <p className="font-bold text-xl text-[#8D6E63]">€{order.total?.toFixed(2)}</p>
                          <Select value={order.status} onValueChange={(val) => handleUpdateOrderStatus(order.id, val)}>
                            <SelectTrigger className={`h-7 text-xs mt-1 ${
                              order.status === 'confirmed' ? 'bg-green-100 text-green-700 border-green-200' :
                              order.status === 'preparing' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                              order.status === 'ready' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                              order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                              order.status === 'cancelled' ? 'bg-red-100 text-red-700 border-red-200' :
                              'bg-amber-100 text-amber-700 border-amber-200'
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending_payment">⏳ Pendente</SelectItem>
                              <SelectItem value="confirmed">✅ Confirmado</SelectItem>
                              <SelectItem value="preparing">👨‍🍳 Preparando</SelectItem>
                              <SelectItem value="ready">📦 Pronto</SelectItem>
                              <SelectItem value="delivered">🎉 Entregue</SelectItem>
                              <SelectItem value="cancelled">❌ Cancelado</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {expandedOrder === order.id ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedOrder === order.id && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                          className="border-t border-stone-100">
                          
                          {/* Quick Actions Bar */}
                          <div className="px-4 py-3 bg-gradient-to-r from-stone-50 to-stone-100 flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-stone-500 mr-2">Ações rápidas:</span>
                            <a
                              href={`tel:${order.contact_phone}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-full transition-colors"
                            >
                              <Phone className="w-3.5 h-3.5" />
                              Ligar
                            </a>
                            <a
                              href={`https://wa.me/${order.contact_phone?.replace(/\D/g, '')}?text=Olá ${order.contact_name}! Sobre seu pedido na Mad Brothers Bakery...`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] hover:bg-[#20BA5A] text-white text-xs font-medium rounded-full transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              WhatsApp
                            </a>
                            {order.contact_email && (
                              <a
                                href={`mailto:${order.contact_email}?subject=Seu pedido na Mad Brothers Bakery - ${order.id.slice(0, 8)}&body=Olá ${order.contact_name},%0D%0A%0D%0ASobre seu pedido de €${order.total?.toFixed(2)} para recolha em ${order.pickup_date} às ${order.pickup_time}...`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-full transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                Email
                              </a>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(`Pedido: ${order.contact_name}\nTel: ${order.contact_phone}\n${order.contact_email ? `Email: ${order.contact_email}\n` : ''}Recolha: ${order.pickup_date} às ${order.pickup_time}\nTotal: €${order.total?.toFixed(2)}`);
                                toast.success('Informações copiadas!');
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-600 hover:bg-stone-700 text-white text-xs font-medium rounded-full transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                              </svg>
                              Copiar
                            </button>
                          </div>
                          
                          {/* Order Details Grid */}
                          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Items */}
                            <div>
                              <h4 className="font-semibold text-[#1a1a1a] mb-3 flex items-center gap-2">
                                <ShoppingBag className="w-4 h-4 text-[#8D6E63]" />
                                Itens do Pedido
                              </h4>
                              <div className="bg-stone-50 rounded-lg p-3">
                                {order.items?.map((item, idx) => {
                                  const itemImg = item.image && (item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`);
                                  return (
                                    <div key={idx} className="flex gap-3 py-3 text-sm border-b border-stone-200 last:border-0" data-testid={`order-item-${idx}`}>
                                      {itemImg ? (
                                        <img
                                          src={itemImg}
                                          alt={item.product_name}
                                          className="w-16 h-16 rounded-md object-cover flex-shrink-0 border border-stone-200"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className="w-16 h-16 rounded-md bg-stone-200 flex items-center justify-center flex-shrink-0">
                                          <ShoppingBag className="w-5 h-5 text-stone-400" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex justify-between gap-2">
                                          <div className="min-w-0">
                                            <span className="font-medium">{item.quantity}x {item.product_name}</span>
                                            {item.size_name && <span className="text-stone-500 ml-2">({item.size_name})</span>}
                                            {item.flavor_name && <span className="text-purple-600 ml-2">• {item.flavor_name}</span>}
                                          </div>
                                          <span className="font-medium text-[#8D6E63] whitespace-nowrap">€{item.total_price?.toFixed(2)}</span>
                                        </div>
                                        {item.custom_text_full && (
                                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 font-medium">
                                            🎂 {item.custom_text_full}
                                          </p>
                                        )}
                                        {item.cake_message && (
                                          <p className="text-xs text-stone-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 mt-1.5">
                                            <span className="font-medium">Mensagem no bolo:</span> &quot;{item.cake_message}&quot;
                                          </p>
                                        )}
                                        {item.special_request && (
                                          <p className="text-xs text-stone-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-1.5">
                                            <span className="font-medium">Pedido especial:</span> {item.special_request}
                                          </p>
                                        )}
                                        {item.gift_note && !item.cake_message && (
                                          <p className="text-xs text-stone-500 italic mt-1.5">📝 &quot;{item.gift_note}&quot;</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="mt-3 pt-3 border-t-2 border-stone-200">
                                  <div className="flex justify-between text-sm">
                                    <span>Subtotal</span>
                                    <span>€{order.subtotal?.toFixed(2)}</span>
                                  </div>
                                  {order.coupon_code && (
                                    <div className="flex justify-between text-sm text-green-600">
                                      <span>🎟️ Cupom ({order.coupon_code})</span>
                                      <span>-€{order.coupon_discount?.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t border-stone-200">
                                    <span>Total</span>
                                    <span className="text-[#8D6E63]">€{order.total?.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Customer Info */}
                            <div>
                              <h4 className="font-semibold text-[#1a1a1a] mb-3 flex items-center gap-2">
                                <User className="w-4 h-4 text-[#8D6E63]" />
                                Informações do Cliente
                              </h4>
                              <div className="bg-stone-50 rounded-lg p-3 space-y-3">
                                <div className="flex items-center gap-3 p-2 bg-white rounded-lg">
                                  <div className="w-10 h-10 bg-[#8D6E63] rounded-full flex items-center justify-center text-white font-bold">
                                    {order.contact_name?.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-[#1a1a1a]">{order.contact_name}</p>
                                    <p className="text-xs text-stone-500">Cliente</p>
                                  </div>
                                </div>
                                
                                <div className="space-y-2">
                                  <a href={`tel:${order.contact_phone}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-green-600 transition-colors">
                                    <Phone className="w-4 h-4" />
                                    <span className="font-medium">{order.contact_phone}</span>
                                  </a>
                                  
                                  {order.contact_email && (
                                    <a href={`mailto:${order.contact_email}`} className="flex items-center gap-2 text-sm text-stone-600 hover:text-blue-600 transition-colors">
                                      <Mail className="w-4 h-4" />
                                      <span className="font-medium">{order.contact_email}</span>
                                    </a>
                                  )}
                                </div>
                                
                                {order.special_instructions && (
                                  <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                                    <p className="text-xs font-medium text-amber-700 mb-1">📝 Instruções especiais:</p>
                                    <p className="text-sm text-amber-800">{order.special_instructions}</p>
                                  </div>
                                )}
                                
                                <div className="pt-2 border-t border-stone-200 text-xs text-stone-400 space-y-1">
                                  <p>🆔 ID: {order.id}</p>
                                  <p>📅 Criado: {new Date(order.created_at).toLocaleString('pt-PT')}</p>
                                  {order.payment_intent_id && (
                                    <p>💳 Stripe: {order.payment_intent_id.slice(0, 20)}...</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== CALENDAR ==================== */}
          <TabsContent value="calendar">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-stone-100 p-6">
                <h3 className="font-semibold text-lg mb-4">Bloquear Datas</h3>
                <p className="text-sm text-stone-500 mb-4">
                  Selecione datas onde não aceita pedidos (feriados, dias lotados, etc.)
                </p>
                <CalendarComponent
                  mode="single"
                  selected={selectedBlackoutDate}
                  onSelect={setSelectedBlackoutDate}
                  disabled={(date) => date < new Date()}
                  modifiers={{
                    blackout: blackoutDates.map(d => new Date(d.date))
                  }}
                  modifiersStyles={{
                    blackout: { backgroundColor: '#FEE2E2', color: '#DC2626' }
                  }}
                  className="rounded-md border"
                />
                {selectedBlackoutDate && (
                  <div className="mt-4 space-y-3">
                    <Input
                      placeholder="Motivo (opcional)"
                      value={blackoutReason}
                      onChange={(e) => setBlackoutReason(e.target.value)}
                    />
                    <Button onClick={handleAddBlackoutDate} className="w-full bg-red-600 hover:bg-red-700">
                      Bloquear {selectedBlackoutDate.toLocaleDateString('pt-PT')}
                    </Button>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-stone-100 p-6">
                <h3 className="font-semibold text-lg mb-4">Datas Bloqueadas</h3>
                {blackoutDates.length === 0 ? (
                  <p className="text-stone-500 text-center py-8">Nenhuma data bloqueada</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {blackoutDates.sort((a, b) => new Date(a.date) - new Date(b.date)).map((bd) => (
                      <div key={bd.date} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium text-red-700">
                            {new Date(bd.date).toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                          {bd.reason && <p className="text-sm text-red-600">{bd.reason}</p>}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveBlackoutDate(bd.date)}
                          className="text-red-600 hover:bg-red-100">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-stone-200">
                  <h4 className="font-medium mb-3">Tempo Mínimo de Preparação</h4>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      value={settingsForm.lead_time_hours || 48}
                      onChange={(e) => setSettingsForm({ ...settingsForm, lead_time_hours: parseInt(e.target.value) })}
                      className="w-24"
                    />
                    <span className="text-stone-500">horas</span>
                    <Button variant="outline" onClick={handleSaveSettings}>Guardar</Button>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    Clientes precisam encomendar com pelo menos {settingsForm.lead_time_hours || 48}h de antecedência
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ==================== COUPONS ==================== */}
          <TabsContent value="coupons">
            <div className="flex justify-end mb-4">
              <Button onClick={handleNewCoupon} className="bg-[#8D6E63] hover:bg-[#8D6E63]/90 rounded-full">
                <Plus className="w-4 h-4 mr-2" /> Novo Cupão
              </Button>
            </div>
            
            {coupons.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <Ticket className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhum cupão criado</p>
                <p className="text-sm text-stone-400 mt-2">Crie cupões de desconto para seus clientes</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {coupons.map((coupon) => (
                  <motion.div key={coupon.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-xl shadow-sm border p-4 ${coupon.active ? 'border-stone-100' : 'border-red-200 bg-red-50/50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                          <Ticket className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-lg text-[#1a1a1a] font-mono">{coupon.code}</h3>
                            {!coupon.active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inativo</span>}
                          </div>
                          <p className="text-[#8D6E63] font-medium">
                            {coupon.discount_type === 'percentage' ? `${coupon.discount_value}% OFF` : `€${coupon.discount_value} OFF`}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-stone-500">
                            {coupon.min_order > 0 && <span>Min: €{coupon.min_order}</span>}
                            {coupon.max_uses && <span>Usos: {coupon.uses_count || 0}/{coupon.max_uses}</span>}
                            {coupon.expires_at && <span>Expira: {new Date(coupon.expires_at).toLocaleDateString('pt-PT')}</span>}
                            {coupon.first_purchase_only && <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">1ª Compra</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={() => handleEditCoupon(coupon)} className="rounded-full">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteCoupon(coupon.id)} className="rounded-full text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== MESSAGES ==================== */}
          <TabsContent value="messages">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a]">
                {messages.length} mensagem{messages.length === 1 ? '' : 's'}
              </h3>
              <Button onClick={fetchMessages} variant="outline" className="rounded-full" data-testid="messages-refresh">
                Atualizar
              </Button>
            </div>

            {messages.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhuma mensagem ainda</p>
                <p className="text-sm text-stone-400 mt-2">As mensagens enviadas pelo formulário Contact aparecem aqui.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-xl border ${m.read ? 'border-stone-100' : 'border-[#D4AF37] shadow-sm'} p-4`}
                    data-testid={`message-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#1a1a1a] truncate">{m.name}</span>
                          {!m.read && (
                            <span className="text-[10px] uppercase tracking-wide font-bold bg-[#D4AF37] text-white px-2 py-0.5 rounded-full">
                              Nova
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-stone-500 mt-1">
                          <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-[#8D6E63]">
                            <Mail className="w-3 h-3" /> {m.email}
                          </a>
                          <span>{new Date(m.created_at).toLocaleString('pt-PT')}</span>
                        </div>
                        <div className="mt-3 text-sm font-medium text-[#1a1a1a]">{m.subject}</div>
                        <p className="mt-1 text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="icon"
                          className="rounded-full"
                          title={m.read ? 'Marcar como não lida' : 'Marcar como lida'}
                          onClick={() => handleToggleMessageRead(m)}
                          data-testid={`message-toggle-read-${m.id}`}
                        >
                          {m.read ? <Mail className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteMessage(m.id)}
                          data-testid={`message-delete-${m.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ==================== PRODUCT DIALOG ==================== */}
      <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
        <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl">
              {isNewProduct ? 'Novo Produto' : 'Editar Produto'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Nome do Produto</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Bolo de Chocolate" className="mt-1" />
              </div>
              
              <div className="sm:col-span-2">
                <Label>Descrição</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descreva o produto..." className="mt-1" rows={3} />
              </div>

              <div>
                <Label>Categoria</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.active).map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Preço Base (€)</Label>
                <Input type="number" step="0.01" min="0" value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })} className="mt-1" />
              </div>

              <div className="sm:col-span-2">
                <Label>Imagem do Produto</Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-4">
                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-full h-24 border-dashed border-2 hover:bg-stone-50"
                    >
                      {uploadingImage ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-6 h-6 animate-spin text-[#8D6E63]" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-6 h-6 text-stone-400" />
                          <span className="text-sm text-stone-500">Click to upload main image</span>
                          <span className="text-xs text-stone-400">JPG, PNG, WebP (max 5MB)</span>
                        </div>
                      )}
                    </Button>
                  </div>

                  {/* Main Image Preview */}
                  {formData.image && (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-[#8D6E63] flex-shrink-0">
                      <img src={formData.image} alt="Main" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image: '' })}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">Main</span>
                    </div>
                  )}
                </div>
                
                {/* Optional: URL input as fallback */}
                <details className="mt-3">
                  <summary className="text-xs text-stone-400 cursor-pointer hover:text-stone-600">
                    Or enter image URL manually
                  </summary>
                  <Input 
                    value={formData.image} 
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://..." 
                    className="mt-2 text-sm" 
                  />
                </details>
              </div>

              {/* Additional Images */}
              <div className="sm:col-span-2">
                <Label>Additional Images (Gallery)</Label>
                <p className="text-xs text-stone-500 mb-2">Add more photos to show different angles of the product</p>
                
                <div className="flex flex-wrap gap-3 mt-2">
                  {/* Existing additional images */}
                  {formData.images && formData.images.map((img, index) => (
                    <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden border border-stone-200 flex-shrink-0">
                      <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add more button */}
                  <label className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center cursor-pointer hover:bg-stone-50 transition-colors">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleAdditionalImageUpload}
                      className="hidden"
                    />
                    <Plus className="w-5 h-5 text-stone-400" />
                    <span className="text-[10px] text-stone-400 mt-1">Add</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Sizes — canonical visual options OR cupcake pack mode */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Label className="block">Variante do seletor</Label>
                <div className="flex gap-2">
                  {[
                    { id: 'cake', label: '🎂 Bolo (6"/8"/10"/12")' },
                    { id: 'cupcake_box', label: '🧁 Caixa de cupcakes' },
                  ].map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, size_variant: v.id, sizes: [] })}
                      className={`text-xs px-3 py-1.5 rounded-full font-medium border transition-all ${
                        (formData.size_variant || 'cake') === v.id
                          ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]'
                          : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <Label className="mb-1 block text-xs">Rótulo personalizado (opcional)</Label>
              <Input
                value={formData.size_label || ''}
                onChange={(e) => setFormData({ ...formData, size_label: e.target.value })}
                placeholder={(formData.size_variant === 'cupcake_box') ? 'Ex: "How many cupcakes?"' : 'Padrão: "Choose Your Size"'}
                className="mb-4"
              />

              {/* CUPCAKE-BOX MODE: quick toggles for 6/12/24 + free-form list for custom counts */}
              {formData.size_variant === 'cupcake_box' ? (
                <div className="space-y-2">
                  <Label className="block text-sm">Opções de quantidade</Label>
                  <p className="text-xs text-stone-500 mb-2">
                    Marque as quantidades que este produto oferece. O preço final = Preço base + Ajuste.
                  </p>

                  {/* Pre-defined pack sizes (6, 12, 24) — same UX pattern as cake sizes */}
                  {[
                    { id: '6_pack',  label: '6 cupcakes'  },
                    { id: '12_pack', label: '12 cupcakes' },
                    { id: '24_pack', label: '24 cupcakes' },
                  ].map((opt) => {
                    const idx = formData.sizes.findIndex((s) => s.id === opt.id);
                    const enabled = idx >= 0;
                    const size = enabled ? formData.sizes[idx] : null;
                    return (
                      <div
                        key={opt.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          enabled ? 'border-[#8D6E63] bg-[#FFFBEF]' : 'border-stone-200 bg-white'
                        }`}
                      >
                        <Switch
                          checked={enabled}
                          data-testid={`pack-toggle-${opt.id}`}
                          onCheckedChange={(v) => {
                            if (v) {
                              setFormData({
                                ...formData,
                                sizes: [
                                  ...formData.sizes,
                                  { id: opt.id, name: opt.label, price_modifier: 0, in_stock: true },
                                ],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                sizes: formData.sizes.filter((s) => s.id !== opt.id),
                              });
                            }
                          }}
                        />
                        <span className={`flex-1 text-sm ${enabled ? 'font-medium text-[#1a1a1a]' : 'text-stone-600'}`}>
                          🧁 {opt.label}
                        </span>
                        {enabled && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-stone-500">+€</span>
                            <Input
                              type="number"
                              step="0.50"
                              value={size.price_modifier || 0}
                              onChange={(e) => {
                                const newSizes = [...formData.sizes];
                                newSizes[idx] = { ...size, price_modifier: parseFloat(e.target.value) || 0 };
                                setFormData({ ...formData, sizes: newSizes });
                              }}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Custom pack sizes (e.g. 4-pack, 18-pack) */}
                  {formData.sizes.filter((s) => !['6_pack', '12_pack', '24_pack'].includes(s.id)).length > 0 && (
                    <div className="pt-3 mt-3 border-t border-stone-200">
                      <p className="text-xs text-stone-500 mb-2">Opções personalizadas:</p>
                      {formData.sizes
                        .map((sz, idx) => ({ sz, idx }))
                        .filter(({ sz }) => !['6_pack', '12_pack', '24_pack'].includes(sz.id))
                        .map(({ sz, idx }) => (
                          <div key={idx} className="flex flex-wrap gap-2 items-end p-2 border border-stone-200 rounded-lg bg-stone-50 mb-2">
                            <div className="flex-1 min-w-[120px]">
                              <Label className="text-[10px] uppercase text-stone-500">Nome visível</Label>
                              <Input
                                value={sz.name || ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const newSizes = [...formData.sizes];
                                  newSizes[idx] = { ...sz, name: v, id: sz.id || v.toLowerCase().replace(/[^a-z0-9]+/g, '_') };
                                  setFormData({ ...formData, sizes: newSizes });
                                }}
                                placeholder="Ex: 18 cupcakes"
                                className="h-9"
                              />
                            </div>
                            <div className="w-28">
                              <Label className="text-[10px] uppercase text-stone-500">+/- Preço (€)</Label>
                              <Input
                                type="number"
                                step="0.50"
                                value={sz.price_modifier || 0}
                                onChange={(e) => {
                                  const newSizes = [...formData.sizes];
                                  newSizes[idx] = { ...sz, price_modifier: parseFloat(e.target.value) || 0 };
                                  setFormData({ ...formData, sizes: newSizes });
                                }}
                                className="h-9"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setFormData({ ...formData, sizes: formData.sizes.filter((_, i) => i !== idx) })}
                              className="h-9 px-3 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({
                      ...formData,
                      sizes: [...(formData.sizes || []), { id: '', name: '', price_modifier: 0, in_stock: true }],
                    })}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Adicionar quantidade personalizada
                  </Button>
                </div>
              ) : (
                <>
                  <Label className="mb-2 block">Tamanhos disponíveis</Label>
                  <p className="text-xs text-stone-500 mb-3">
                    Marque os tamanhos que este produto oferece e defina o preço de cada. Deixe tudo desmarcado se o produto tem só um tamanho (usa apenas o &quot;Preço base&quot;).
                  </p>
                  <div className="space-y-2">
                    {[
                      { id: '6_round',  label: '6" Round',   shape: 'round'  },
                      { id: '8_round',  label: '8" Round',   shape: 'round'  },
                      { id: '10_round', label: '10" Round',  shape: 'round'  },
                      { id: '12_square',label: '12" Square', shape: 'square' },
                    ].map((opt) => {
                      const idx = formData.sizes.findIndex((s) => s.id === opt.id);
                      const enabled = idx >= 0;
                  const current = enabled ? formData.sizes[idx] : null;
                  // Absolute price in admin (more intuitive). Stored as price_modifier = price - base_price.
                  const absolutePrice = enabled
                    ? Number(formData.base_price || 0) + Number(current.price_modifier || 0)
                    : '';

                  const toggle = (checked) => {
                    if (checked) {
                      setFormData({
                        ...formData,
                        sizes: [
                          ...formData.sizes,
                          { id: opt.id, name: opt.label, price_modifier: 0, in_stock: true },
                        ],
                      });
                    } else {
                      setFormData({
                        ...formData,
                        sizes: formData.sizes.filter((s) => s.id !== opt.id),
                      });
                    }
                  };

                  const updatePrice = (val) => {
                    const newAbs = Number(val);
                    const base = Number(formData.base_price || 0);
                    const newSizes = [...formData.sizes];
                    newSizes[idx] = { ...newSizes[idx], price_modifier: newAbs - base };
                    setFormData({ ...formData, sizes: newSizes });
                  };

                  const updateStock = (val) => {
                    const newSizes = [...formData.sizes];
                    newSizes[idx] = { ...newSizes[idx], in_stock: val };
                    setFormData({ ...formData, sizes: newSizes });
                  };

                  return (
                    <div
                      key={opt.id}
                      className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border ${
                        enabled ? 'border-[#D4AF37] bg-[#FFFBEF]' : 'border-stone-200 bg-stone-50'
                      }`}
                      data-testid={`admin-size-row-${opt.id}`}
                    >
                      <Switch checked={enabled} onCheckedChange={toggle} data-testid={`admin-size-toggle-${opt.id}`} />
                      <span className="font-medium text-[#1a1a1a] min-w-[120px]">{opt.label}</span>
                      {enabled && (
                        <>
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-stone-500">€</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Preço"
                              value={absolutePrice}
                              onChange={(e) => updatePrice(e.target.value)}
                              className="w-28"
                              data-testid={`admin-size-price-${opt.id}`}
                            />
                          </div>
                          <div className="flex items-center gap-1 ml-auto">
                            <Switch
                              checked={current.in_stock !== false}
                              onCheckedChange={updateStock}
                            />
                            <span className="text-xs text-stone-500">Em estoque</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Show any legacy/custom sizes that aren't part of the canonical 4 */}
              {formData.size_variant !== 'cupcake_box' && formData.sizes.filter((s) => !['6_round', '8_round', '10_round', '12_square'].includes(s.id)).length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-200">
                  <p className="text-xs text-stone-500 mb-2">Tamanhos personalizados (legacy):</p>
                  {formData.sizes
                    .map((s, idx) => ({ s, idx }))
                    .filter(({ s }) => !['6_round', '8_round', '10_round', '12_square'].includes(s.id))
                    .map(({ s, idx }) => (
                      <div key={idx} className="flex gap-2 mb-2 items-center flex-wrap">
                        <Input placeholder="Nome" value={s.name}
                          onChange={(e) => updateSize(idx, 'name', e.target.value)} className="flex-1 min-w-[150px]" />
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-stone-500">+€</span>
                          <Input type="number" value={s.price_modifier}
                            onChange={(e) => updateSize(idx, 'price_modifier', e.target.value)} className="w-20" />
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeSize(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                </div>
              )}
                </>
              )}
            </div>

            {/* Flavors with stock */}
            <div>
              <Label className="mb-2 block">Sabores disponíveis</Label>
              <p className="text-xs text-stone-500 mb-3">
                Marque quais sabores este produto oferece e ajuste o preço extra de cada (0 se não muda).
              </p>
              <div className="space-y-2">
                {[
                  { id: 'vanilla', name: 'Vanilla' },
                  { id: 'swiss_chocolate_ganache', name: 'Swiss Chocolate Ganache' },
                  { id: 'chocolate_buttercream', name: 'Chocolate Buttercream' },
                  { id: 'strawberry_gateaux', name: 'Strawberry Gateaux' },
                  { id: 'black_forest', name: 'Black Forest' },
                  { id: 'pistachio', name: 'Pistachio' },
                  { id: 'mixed_fruit', name: 'Mixed Fruit' },
                  { id: 'lemon', name: 'Lemon' },
                  { id: 'biscoff_lotus', name: 'Biscoff Lotus' },
                  { id: 'cookies_and_cream', name: 'Cookies & Cream' },
                ].map((opt) => {
                  const idx = formData.flavors.findIndex((f) => f.id === opt.id);
                  const enabled = idx >= 0;
                  const flavor = enabled ? formData.flavors[idx] : null;
                  return (
                    <div
                      key={opt.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                        enabled ? 'border-[#8D6E63] bg-[#FFFBEF]' : 'border-stone-200 bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        data-testid={`flavor-toggle-${opt.id}`}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              flavors: [
                                ...formData.flavors,
                                { id: opt.id, name: opt.name, price_modifier: 0, in_stock: true },
                              ],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              flavors: formData.flavors.filter((f) => f.id !== opt.id),
                            });
                          }
                        }}
                        className="w-4 h-4 accent-[#8D6E63] flex-shrink-0"
                      />
                      <span className={`flex-1 text-sm ${enabled ? 'font-medium text-[#1a1a1a]' : 'text-stone-600'}`}>
                        {opt.name}
                      </span>
                      {enabled && (
                        <>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-stone-500">+€</span>
                            <Input
                              type="number"
                              step="0.50"
                              value={flavor.price_modifier || 0}
                              onChange={(e) => {
                                const newFlavors = [...formData.flavors];
                                newFlavors[idx] = { ...flavor, price_modifier: parseFloat(e.target.value) || 0 };
                                setFormData({ ...formData, flavors: newFlavors });
                              }}
                              className="w-20 h-8 text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Switch
                              checked={flavor.in_stock !== false}
                              onCheckedChange={(v) => {
                                const newFlavors = [...formData.flavors];
                                newFlavors[idx] = { ...flavor, in_stock: v };
                                setFormData({ ...formData, flavors: newFlavors });
                              }}
                            />
                            <span className="text-xs text-stone-500">Stock</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legacy / custom flavors (anything not in the canonical list) */}
              {formData.flavors.filter((f) => ![
                'vanilla','swiss_chocolate_ganache','chocolate_buttercream','strawberry_gateaux',
                'black_forest','pistachio','mixed_fruit','lemon','biscoff_lotus','cookies_and_cream'
              ].includes(f.id)).length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-200">
                  <p className="text-xs text-stone-500 mb-2">Sabores personalizados (legacy):</p>
                  {formData.flavors
                    .map((f, i) => ({ f, i }))
                    .filter(({ f }) => ![
                      'vanilla','swiss_chocolate_ganache','chocolate_buttercream','strawberry_gateaux',
                      'black_forest','pistachio','mixed_fruit','lemon','biscoff_lotus','cookies_and_cream'
                    ].includes(f.id))
                    .map(({ f, i }) => (
                      <div key={i} className="flex gap-2 mb-2 items-center">
                        <Input placeholder="Nome" value={f.name}
                          onChange={(e) => updateFlavor(i, 'name', e.target.value)} className="flex-1" />
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-stone-500">+€</span>
                          <Input type="number" value={f.price_modifier}
                            onChange={(e) => updateFlavor(i, 'price_modifier', e.target.value)} className="w-20" />
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeFlavor(i)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                </div>
              )}

              <Button type="button" variant="outline" size="sm" onClick={addFlavor} className="mt-3">
                <Plus className="w-3 h-3 mr-1" /> Adicionar sabor personalizado
              </Button>
            </div>

            {/* Allergens */}
            <div>
              <Label className="mb-2 block">Alergénios</Label>
              <div className="flex flex-wrap gap-2">
                {ALLERGENS.map((allergen) => (
                  <button key={allergen} type="button" onClick={() => toggleAllergen(allergen)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      formData.allergens.includes(allergen)
                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
                    }`}>
                    {allergen}
                  </button>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch checked={formData.customizable}
                  onCheckedChange={(v) => setFormData({ ...formData, customizable: v })} />
                <Label>Personalizável</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.allow_cake_message !== false}
                  onCheckedChange={(v) => setFormData({ ...formData, allow_cake_message: v })}
                  data-testid="admin-allow-cake-message"
                />
                <Label>Cake Top Message</Label>
              </div>
              {formData.allow_cake_message !== false && (
                <div className="flex items-center gap-2">
                  <Label className="whitespace-nowrap">Máx. caracteres:</Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={formData.cake_message_max_length ?? 50}
                    onChange={(e) => setFormData({ ...formData, cake_message_max_length: parseInt(e.target.value, 10) || 50 })}
                    className="w-20"
                    data-testid="admin-cake-message-max-length"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.allow_special_request !== false}
                  onCheckedChange={(v) => setFormData({ ...formData, allow_special_request: v })}
                  data-testid="admin-allow-special-request"
                />
                <Label>Special Request</Label>
              </div>
              {formData.allow_special_request !== false && (
                <div className="flex items-center gap-2">
                  <Label className="whitespace-nowrap">Máx. caracteres:</Label>
                  <Input
                    type="number"
                    min="50"
                    max="1000"
                    value={formData.special_request_max_length ?? 250}
                    onChange={(e) => setFormData({ ...formData, special_request_max_length: parseInt(e.target.value, 10) || 250 })}
                    className="w-20"
                    data-testid="admin-special-request-max-length"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Switch checked={formData.active}
                  onCheckedChange={(v) => setFormData({ ...formData, active: v })} />
                <Label>Ativo</Label>
              </div>
            </div>

            {/* Stock control */}
            <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <Label className="text-sm font-semibold text-[#1a1a1a]">Controle de Estoque</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Defina quantas unidades estão disponíveis. Quando chegar a 0, aparece &quot;ESGOTADO&quot; no site e clientes não conseguem comprar.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.stock_quantity === null || formData.stock_quantity === undefined}
                    onCheckedChange={(v) => setFormData({ ...formData, stock_quantity: v ? null : 0 })}
                    data-testid="admin-unlimited-stock-toggle"
                  />
                  <Label className="text-sm whitespace-nowrap">Estoque ilimitado</Label>
                </div>
              </div>
              {formData.stock_quantity !== null && formData.stock_quantity !== undefined && (
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap text-sm">Unidades em estoque:</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                      setFormData({ ...formData, stock_quantity: isNaN(val) ? 0 : Math.max(0, val) });
                    }}
                    className="w-28"
                    data-testid="admin-stock-quantity-input"
                  />
                  {formData.stock_quantity === 0 && (
                    <span className="text-xs font-bold text-red-600 uppercase tracking-wider">Esgotado</span>
                  )}
                  {formData.stock_quantity > 0 && formData.stock_quantity <= 3 && (
                    <span className="text-xs font-semibold text-amber-600">Estoque baixo</span>
                  )}
                </div>
              )}
            </div>

            {/* Custom Text Template (e.g. Bento cake age picker) */}
            <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-semibold text-[#1a1a1a]">Frase Configurável</Label>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Defina uma frase fixa com uma variável <code>{'{value}'}</code> que o cliente escolhe
                    (ex: idade). Ideal para Bento Cakes onde só o número muda.
                  </p>
                </div>
                <Switch
                  checked={!!formData.custom_text_template?.enabled}
                  onCheckedChange={(v) => setFormData({
                    ...formData,
                    custom_text_template: { ...formData.custom_text_template, enabled: v }
                  })}
                  data-testid="admin-custom-text-toggle"
                />
              </div>

              {formData.custom_text_template?.enabled && (
                <div className="space-y-3 pt-2 border-t border-stone-200">
                  <div>
                    <Label className="text-xs">Rótulo visível ao cliente</Label>
                    <Input
                      value={formData.custom_text_template.label || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        custom_text_template: { ...formData.custom_text_template, label: e.target.value }
                      })}
                      placeholder="Ex: Choose your age"
                      className="mt-1 h-9 text-sm"
                      data-testid="admin-custom-text-label"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Template da frase (use <code>{'{value}'}</code> onde o número entra)</Label>
                    <Input
                      value={formData.custom_text_template.template || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        custom_text_template: { ...formData.custom_text_template, template: e.target.value }
                      })}
                      placeholder="{value} years since mom found you in the bin"
                      className={`mt-1 h-9 text-sm font-mono ${
                        !(formData.custom_text_template.template || '').includes('{value}')
                          ? 'border-red-400 focus-visible:ring-red-400'
                          : ''
                      }`}
                      data-testid="admin-custom-text-template"
                    />
                    {!(formData.custom_text_template.template || '').includes('{value}') && (
                      <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                        <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                        <div className="text-xs text-red-700">
                          <strong>Faltando <code className="bg-red-100 px-1 rounded">{'{value}'}</code> no template.</strong>{' '}
                          Use exatamente <code className="bg-red-100 px-1 rounded">{'{value}'}</code> no lugar onde o número vai aparecer.
                          <div className="mt-1 text-red-600">
                            ✅ Certo: <code className="bg-white px-1 rounded">{'{value} years since mom found you in the bin'}</code>
                            <br />
                            ❌ Errado: <code className="bg-white px-1 rounded">{'{years since mom found you} value'}</code>
                          </div>
                          <p className="mt-1 text-red-500">Sem isso, o dropdown não aparece para o cliente.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs">Como o cliente escolhe</Label>
                    <select
                      value={formData.custom_text_template.mode || 'list'}
                      onChange={(e) => setFormData({
                        ...formData,
                        custom_text_template: { ...formData.custom_text_template, mode: e.target.value }
                      })}
                      className="mt-1 h-9 w-full text-sm border border-stone-300 rounded-md px-3 bg-white"
                      data-testid="admin-custom-text-mode"
                    >
                      <option value="list">Lista de opções (cliente escolhe entre valores que você define)</option>
                      <option value="range">Faixa numérica (cliente digita um número entre min e max)</option>
                      <option value="text">Texto livre (cliente digita letras, ex: nome)</option>
                    </select>
                  </div>

                  {formData.custom_text_template.mode === 'list' && (
                    <div>
                      <Label className="text-xs">Opções disponíveis (separadas por vírgula)</Label>
                      <Input
                        value={(formData.custom_text_template.options || []).join(', ')}
                        onChange={(e) => setFormData({
                          ...formData,
                          custom_text_template: {
                            ...formData.custom_text_template,
                            options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          }
                        })}
                        placeholder="18, 21, 25, 30, 40, 50, 60"
                        className="mt-1 h-9 text-sm"
                        data-testid="admin-custom-text-options"
                      />
                    </div>
                  )}

                  {formData.custom_text_template.mode === 'range' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Mínimo</Label>
                        <Input
                          type="number"
                          value={formData.custom_text_template.min ?? 1}
                          onChange={(e) => setFormData({
                            ...formData,
                            custom_text_template: { ...formData.custom_text_template, min: parseInt(e.target.value, 10) || 0 }
                          })}
                          className="mt-1 h-9 text-sm"
                          data-testid="admin-custom-text-min"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Máximo</Label>
                        <Input
                          type="number"
                          value={formData.custom_text_template.max ?? 99}
                          onChange={(e) => setFormData({
                            ...formData,
                            custom_text_template: { ...formData.custom_text_template, max: parseInt(e.target.value, 10) || 99 }
                          })}
                          className="mt-1 h-9 text-sm"
                          data-testid="admin-custom-text-max"
                        />
                      </div>
                    </div>
                  )}

                  {formData.custom_text_template.mode === 'text' && (
                    <div>
                      <Label className="text-xs">Limite de letras (máx. caracteres que o cliente pode digitar)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={formData.custom_text_template.max_length ?? 20}
                        onChange={(e) => setFormData({
                          ...formData,
                          custom_text_template: {
                            ...formData.custom_text_template,
                            max_length: parseInt(e.target.value, 10) || 20
                          }
                        })}
                        placeholder="20"
                        className="mt-1 h-9 text-sm w-32"
                        data-testid="admin-custom-text-max-length"
                      />
                      <p className="text-[11px] text-stone-500 mt-1">
                        Recomendamos 15–25 letras para nomes (Sarah, Joao, Maria João, etc.)
                      </p>
                    </div>
                  )}

                  <div className="rounded-lg bg-white border border-stone-200 p-3">
                    <p className="text-[11px] text-stone-500 uppercase tracking-wider mb-1">Prévia (com primeiro valor)</p>
                    <p className="font-handwritten text-xl text-[#1a1a1a]" data-testid="admin-custom-text-preview">
                      {(() => {
                        const tpl = formData.custom_text_template;
                        let sample;
                        if (tpl.mode === 'list') sample = tpl.options?.[0] || '21';
                        else if (tpl.mode === 'range') sample = String(tpl.min ?? 21);
                        else sample = 'Sarah'; // text mode
                        return (tpl.template || '').replace(/\{value\}/g, sample) || '(template vazio)';
                      })()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {(() => {
            const tpl = formData.custom_text_template;
            const tplInvalid = tpl?.enabled && !(tpl.template || '').includes('{value}');
            return (
              <div className="flex flex-col items-end gap-2 pt-4 border-t">
                {tplInvalid && (
                  <p className="text-xs text-red-600 font-medium">
                    Corrija o template da Frase Configurável (faltando <code>{'{value}'}</code>) antes de guardar.
                  </p>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setIsProductDialogOpen(false)}>Cancelar</Button>
                  <Button
                    onClick={handleSaveProduct}
                    disabled={tplInvalid}
                    className="bg-[#8D6E63] hover:bg-[#8D6E63]/90 disabled:bg-stone-400 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4 mr-2" /> Guardar
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ==================== CATEGORY DIALOG ==================== */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isNewCategory ? 'Nova Categoria' : 'Editar Categoria'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Categoria</Label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Ex: Bolos de Aniversário" className="mt-1" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Descreva..." className="mt-1" rows={2} />
            </div>
            <div>
              <Label>Imagem de Capa</Label>
              <p className="text-xs text-stone-500 mt-1 mb-2">
                Esta imagem aparece no card da categoria na página /shop. Se vazia, usaremos a foto do primeiro produto.
              </p>
              {categoryForm.image_url ? (
                <div className="relative rounded-lg overflow-hidden border border-stone-200 aspect-[3/4] max-w-[180px]">
                  <img src={categoryForm.image_url} alt="Cover" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setCategoryForm({ ...categoryForm, image_url: '' })}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm shadow-lg"
                    data-testid="remove-category-cover-btn"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full max-w-[180px] aspect-[3/4] border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:bg-stone-50 transition-colors">
                  <Upload className="w-6 h-6 text-stone-400 mb-2" />
                  <span className="text-xs text-stone-500 text-center px-2">
                    {uploadingImage ? 'A carregar...' : 'Clique para enviar'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCategoryImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                    data-testid="category-cover-upload-input"
                  />
                </label>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={categoryForm.active} onCheckedChange={(v) => setCategoryForm({ ...categoryForm, active: v })} />
              <Label>Categoria Ativa</Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCategory} className="bg-[#8D6E63] hover:bg-[#8D6E63]/90">
              <Save className="w-4 h-4 mr-2" /> Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== COUPON DIALOG ==================== */}
      <Dialog open={isCouponDialogOpen} onOpenChange={setIsCouponDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isNewCoupon ? 'Novo Cupão' : 'Editar Cupão'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Código do Cupão</Label>
              <Input value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                placeholder="Ex: MADBRO10" className="mt-1 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Desconto</Label>
                <Select value={couponForm.discount_type} onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor</Label>
                <Input type="number" value={couponForm.discount_value}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_value: parseFloat(e.target.value) || 0 })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pedido Mínimo (€)</Label>
                <Input type="number" value={couponForm.min_order}
                  onChange={(e) => setCouponForm({ ...couponForm, min_order: parseFloat(e.target.value) || 0 })} className="mt-1" />
              </div>
              <div>
                <Label>Máximo de Usos</Label>
                <Input type="number" value={couponForm.max_uses || ''}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Ilimitado" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Data de Expiração</Label>
              <Input type="date" value={couponForm.expires_at?.split('T')[0] || ''}
                onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value ? `${e.target.value}T23:59:59Z` : '' })} className="mt-1" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={couponForm.active} onCheckedChange={(v) => setCouponForm({ ...couponForm, active: v })} />
              <Label>Cupão Ativo</Label>
            </div>
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <Switch checked={couponForm.first_purchase_only} onCheckedChange={(v) => setCouponForm({ ...couponForm, first_purchase_only: v })} />
              <div>
                <Label>Apenas Primeira Compra</Label>
                <p className="text-xs text-blue-600 mt-0.5">Só funciona para utilizadores registados na primeira compra</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsCouponDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCoupon} className="bg-[#8D6E63] hover:bg-[#8D6E63]/90">
              <Save className="w-4 h-4 mr-2" /> Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== CHANGE PASSWORD DIALOG ==================== */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-[#8D6E63]" />
              Alterar Senha
            </DialogTitle>
            <DialogDescription>
              Para sua segurança, escolha uma senha forte com no mínimo 8 caracteres.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm">Senha atual</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPwd ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Digite sua senha atual"
                  data-testid="current-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  tabIndex={-1}
                >
                  {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm">Nova senha</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPwd ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  data-testid="new-password-input"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd(!showNewPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  tabIndex={-1}
                >
                  {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm">Confirmar nova senha</Label>
              <Input
                type={showNewPwd ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Repita a nova senha"
                data-testid="confirm-password-input"
                className="mt-1"
              />
              {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setIsPasswordDialogOpen(false)}
              disabled={changingPassword}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="bg-[#8D6E63] hover:bg-[#8D6E63]/90"
              data-testid="submit-password-change-btn"
            >
              {changingPassword ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Alterando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Alterar Senha</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* ==================== SYNC TO PRODUCTION DIALOG ==================== */}
      <Dialog open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl flex items-center gap-2">
              <Upload className="w-6 h-6 text-[#D4AF37]" />
              Sincronizar com Produção
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Atenção:</strong> isso vai <strong>substituir</strong> os produtos, categorias, cupons e datas
              de bloqueio no site de produção pelos dados que estão aqui no preview. Pedidos e mensagens
              em produção <strong>não</strong> são afetados.
            </div>

            <div>
              <Label className="text-sm">URL de Produção</Label>
              <Input
                value={syncForm.production_url}
                onChange={(e) => setSyncForm({ ...syncForm, production_url: e.target.value })}
                className="mt-1"
                placeholder="https://...emergent.host"
                data-testid="sync-prod-url"
              />
            </div>

            <div>
              <Label className="text-sm">Senha de Admin (produção)</Label>
              <Input
                type="password"
                value={syncForm.admin_password}
                onChange={(e) => setSyncForm({ ...syncForm, admin_password: e.target.value })}
                className="mt-1"
                placeholder="Digite a senha do admin de produção"
                data-testid="sync-prod-password"
                autoComplete="off"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                Mesmo email <code>madbrothersdublin@gmail.com</code>. A senha é usada uma vez e descartada.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsSyncDialogOpen(false)} disabled={syncing}>
              Cancelar
            </Button>
            <Button
              onClick={handleSyncFromProduction}
              disabled={syncing}
              variant="outline"
              className="border-[#8D6E63] text-[#8D6E63] hover:bg-[#8D6E63] hover:text-white"
              data-testid="sync-from-prod-confirm"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A puxar...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Puxar de Produção</>
              )}
            </Button>
            <Button
              onClick={handleSyncToProduction}
              disabled={syncing}
              className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#1a1a1a]"
              data-testid="sync-prod-confirm"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A sincronizar...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Enviar para Produção</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== SETTINGS DIALOG ==================== */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl">Configurações da Loja</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="branding" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="branding">Marca</TabsTrigger>
              <TabsTrigger value="logistics">Logística</TabsTrigger>
              <TabsTrigger value="carousel">Carrossel</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
            </TabsList>

            {/* Branding */}
            <TabsContent value="branding" className="space-y-4">
              {/* Logo Settings Section */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <h4 className="font-semibold text-[#1a1a1a] mb-4 flex items-center gap-2">
                  <Image className="w-4 h-4" /> Configurações do Logo
                </h4>
                
                {/* Logo Upload */}
                <div className="mb-4">
                  <Label>Imagem do Logo</Label>
                  <p className="text-xs text-stone-500 mb-2">Faça upload do seu logo personalizado</p>
                  <div className="flex items-start gap-4">
                    {/* Current Logo Preview */}
                    <div className="w-24 h-24 border-2 border-dashed border-stone-300 rounded-xl flex items-center justify-center overflow-hidden bg-white">
                      {settingsForm.logo_image ? (
                        <img 
                          src={settingsForm.logo_image.startsWith('http') ? settingsForm.logo_image : `${BASE_URL}${settingsForm.logo_image}`}
                          alt="Logo" 
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <ImagePlus className="w-8 h-8 text-stone-400" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="logo-upload"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setUploadingImage(true);
                          const formDataUpload = new FormData();
                          formDataUpload.append('file', file);
                          try {
                            const res = await axios.post(`${API}/upload`, formDataUpload, {
                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                            });
                            setSettingsForm({ ...settingsForm, logo_image: res.data.url });
                            toast.success('Logo carregado!');
                          } catch (err) {
                            toast.error('Erro ao carregar logo');
                          }
                          setUploadingImage(false);
                        }}
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingImage}
                        className="w-full"
                      >
                        {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Carregar Logo
                      </Button>
                      {/* Quick select from available logos */}
                      <div className="mt-3">
                        <p className="text-xs text-stone-500 mb-2">Logos disponíveis:</p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setSettingsForm({ ...settingsForm, logo_image: '/api/uploads/logo_full_transparent.png' })}
                            className={`w-16 h-16 border-2 rounded-xl overflow-hidden bg-stone-800 p-1 transition-all hover:scale-105 ${settingsForm.logo_image?.includes('logo_full') ? 'border-[#8D6E63] ring-2 ring-[#8D6E63]/20' : 'border-stone-200 hover:border-stone-300'}`}
                            title="Logo Completo (fundo escuro)"
                          >
                            <img src={`${BASE_URL}/api/uploads/logo_full_transparent.png`} alt="Logo Completo" className="w-full h-full object-contain" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setSettingsForm({ ...settingsForm, logo_image: '/api/uploads/logo_simple_transparent.png' })}
                            className={`w-16 h-16 border-2 rounded-xl overflow-hidden bg-white p-1 transition-all hover:scale-105 ${settingsForm.logo_image?.includes('logo_simple') ? 'border-[#8D6E63] ring-2 ring-[#8D6E63]/20' : 'border-stone-200 hover:border-stone-300'}`}
                            title="Logo Simples (fundo claro)"
                          >
                            <img src={`${BASE_URL}/api/uploads/logo_simple_transparent.png`} alt="Logo Simples" className="w-full h-full object-contain" />
                          </button>
                        </div>
                        <p className="text-xs text-stone-400 mt-2">Clique para selecionar • Logos com fundo transparente</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logo Display Options */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Tamanho do Logo</Label>
                    <Select 
                      value={settingsForm.logo_size || 'xl'} 
                      onValueChange={(v) => setSettingsForm({ ...settingsForm, logo_size: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sm">Pequeno</SelectItem>
                        <SelectItem value="md">Médio</SelectItem>
                        <SelectItem value="lg">Grande</SelectItem>
                        <SelectItem value="xl">Extra Grande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Forma do Logo</Label>
                    <Select 
                      value={settingsForm.logo_shape || 'circle'} 
                      onValueChange={(v) => setSettingsForm({ ...settingsForm, logo_shape: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circle">Circular</SelectItem>
                        <SelectItem value="rounded">Arredondado</SelectItem>
                        <SelectItem value="square">Quadrado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-stone-200">
                  <div>
                    <Label>Mostrar Texto &quot;Mad Brothers&quot;</Label>
                    <p className="text-xs text-stone-500 mt-1">Exibir o nome da marca ao lado do logo</p>
                  </div>
                  <Switch 
                    checked={settingsForm.show_logo_text !== false}
                    onCheckedChange={(v) => setSettingsForm({ ...settingsForm, show_logo_text: v })} 
                  />
                </div>

                {/* Live Preview */}
                <div className="mt-4 p-6 bg-gradient-to-br from-stone-100 to-stone-50 rounded-xl border border-stone-200">
                  <Label className="text-xs text-stone-500 mb-3 block uppercase tracking-wider">Pré-visualização</Label>
                  <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
                    <img 
                      src={settingsForm.logo_image ? (settingsForm.logo_image.startsWith('http') ? settingsForm.logo_image : `${BASE_URL}${settingsForm.logo_image}`) : `${BASE_URL}/api/uploads/logo_full.png`}
                      alt="Logo Preview"
                      className={`
                        ${settingsForm.logo_size === 'sm' ? 'h-12 w-12' : ''}
                        ${settingsForm.logo_size === 'md' ? 'h-16 w-16' : ''}
                        ${settingsForm.logo_size === 'lg' ? 'h-20 w-20' : ''}
                        ${(!settingsForm.logo_size || settingsForm.logo_size === 'xl') ? 'h-24 w-24' : ''}
                        object-contain shadow-md
                        ${settingsForm.logo_shape === 'circle' ? 'rounded-full' : ''}
                        ${settingsForm.logo_shape === 'rounded' ? 'rounded-xl' : ''}
                        ${settingsForm.logo_shape === 'square' ? 'rounded-none' : ''}
                      `}
                    />
                    {settingsForm.show_logo_text !== false && (
                      <div className="flex flex-col leading-tight">
                        <span className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a]">Mad Brothers</span>
                        <span className="font-['Poppins'] text-xs tracking-[0.25em] uppercase font-medium text-[#8D6E63]">Bakery</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>Nome do Negócio</Label>
                <Input value={settingsForm.business_name || ''} onChange={(e) => setSettingsForm({ ...settingsForm, business_name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input value={settingsForm.tagline || ''} onChange={(e) => setSettingsForm({ ...settingsForm, tagline: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Sobre Nós</Label>
                <Textarea value={settingsForm.about_us || ''} onChange={(e) => setSettingsForm({ ...settingsForm, about_us: e.target.value })}
                  className="mt-1" rows={5} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Instagram URL</Label>
                  <Input value={settingsForm.social_instagram || ''} onChange={(e) => setSettingsForm({ ...settingsForm, social_instagram: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Facebook URL</Label>
                  <Input value={settingsForm.social_facebook || ''} onChange={(e) => setSettingsForm({ ...settingsForm, social_facebook: e.target.value })} className="mt-1" />
                </div>
              </div>
            </TabsContent>

            {/* Logistics */}
            <TabsContent value="logistics" className="space-y-4">
              <div>
                <Label>Endereço</Label>
                <Input value={settingsForm.address || ''} onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Telefone</Label>
                  <Input value={settingsForm.phone || ''} onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={settingsForm.email || ''} onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })} className="mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Lead Time (horas)</Label>
                  <Input type="number" value={settingsForm.lead_time_hours || 48}
                    onChange={(e) => setSettingsForm({ ...settingsForm, lead_time_hours: parseInt(e.target.value) })} className="mt-1" />
                </div>
                <div>
                  <Label>Entrega Grátis (€)</Label>
                  <Input type="number" value={settingsForm.free_delivery_threshold || 50}
                    onChange={(e) => setSettingsForm({ ...settingsForm, free_delivery_threshold: parseFloat(e.target.value) })} className="mt-1" />
                </div>
                <div>
                  <Label>Taxa de Entrega (€)</Label>
                  <Input type="number" value={settingsForm.delivery_fee || 5}
                    onChange={(e) => setSettingsForm({ ...settingsForm, delivery_fee: parseFloat(e.target.value) })} className="mt-1" />
                </div>
              </div>

              {/* Delivery Settings */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <h4 className="font-semibold text-[#1a1a1a] mb-4">Opções de Entrega</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Ativar Entrega (Delivery)</Label>
                      <p className="text-xs text-stone-500 mt-1">Quando ativado, clientes podem escolher delivery</p>
                    </div>
                    <Switch 
                      checked={settingsForm.delivery_enabled || false}
                      onCheckedChange={(v) => setSettingsForm({ ...settingsForm, delivery_enabled: v })} 
                    />
                  </div>
                  <div>
                    <Label>Endereço da Loja (para recolha)</Label>
                    <Input 
                      value={settingsForm.store_address || '17 Nephin Road, Dublin 7, D07 N8K7'} 
                      onChange={(e) => setSettingsForm({ ...settingsForm, store_address: e.target.value })} 
                      className="mt-1" 
                      placeholder="17 Nephin Road, Dublin 7, D07 N8K7"
                    />
                  </div>
                  <div>
                    <Label>Áreas de Entrega (se ativado)</Label>
                    <Input 
                      value={settingsForm.delivery_areas || 'Dublin 7'} 
                      onChange={(e) => setSettingsForm({ ...settingsForm, delivery_areas: e.target.value })} 
                      className="mt-1" 
                      placeholder="Dublin 7, Dublin 15"
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Horários de Recolha</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addPickupSlot}>
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
                {(settingsForm.pickup_slots || []).map((slot, index) => (
                  <div key={index} className="flex gap-2 mb-2 items-center">
                    <Input placeholder="Nome" value={slot.name}
                      onChange={(e) => updatePickupSlot(index, 'name', e.target.value)} className="flex-1" />
                    <Input type="time" value={slot.start}
                      onChange={(e) => updatePickupSlot(index, 'start', e.target.value)} className="w-28" />
                    <span>-</span>
                    <Input type="time" value={slot.end}
                      onChange={(e) => updatePickupSlot(index, 'end', e.target.value)} className="w-28" />
                    <Switch checked={slot.active} onCheckedChange={(v) => updatePickupSlot(index, 'active', v)} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePickupSlot(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Carousel */}
            <TabsContent value="carousel" className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label>Imagens do Carrossel (Homepage)</Label>
                  <p className="text-xs text-stone-500 mt-1">Adicione imagens para o banner principal do site</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addHeroImage}>
                  <Plus className="w-3 h-3 mr-1" /> Adicionar
                </Button>
              </div>
              {(settingsForm.hero_images || []).map((img, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3 bg-stone-50/50">
                  <div className="flex justify-between items-start gap-4">
                    {/* Image upload area */}
                    <div className="w-40 flex-shrink-0">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => handleCarouselImageUpload(e, index)}
                        className="hidden"
                        id={`carousel-upload-${index}`}
                      />
                      {img.url ? (
                        <div className="relative w-full h-24 rounded-lg overflow-hidden border border-stone-200">
                          <img src={img.url} alt="Preview" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => updateHeroImage(index, 'url', '')}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor={`carousel-upload-${index}`}
                          className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:bg-stone-100 transition-colors"
                        >
                          {uploadingCarouselIndex === index ? (
                            <Loader2 className="w-6 h-6 animate-spin text-[#8D6E63]" />
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-stone-400 mb-1" />
                              <span className="text-xs text-stone-500">Enviar</span>
                            </>
                          )}
                        </label>
                      )}
                      {/* URL fallback - small link */}
                      <button
                        type="button"
                        onClick={() => {
                          const url = prompt('Cole a URL da imagem:', img.url || '');
                          if (url !== null) updateHeroImage(index, 'url', url);
                        }}
                        className="text-[10px] text-stone-400 hover:text-stone-600 mt-1 block w-full text-center"
                      >
                        ou usar URL
                      </button>
                    </div>

                    {/* Text fields */}
                    <div className="flex-1 space-y-2">
                      <Input 
                        placeholder="Título (ex: Bolos Personalizados)" 
                        value={img.title}
                        onChange={(e) => updateHeroImage(index, 'title', e.target.value)} 
                      />
                      <Input 
                        placeholder="Subtítulo (ex: Feitos com amor)" 
                        value={img.subtitle}
                        onChange={(e) => updateHeroImage(index, 'subtitle', e.target.value)} 
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col items-center gap-2">
                      <Switch checked={img.active} onCheckedChange={(v) => updateHeroImage(index, 'active', v)} />
                      <span className="text-[10px] text-stone-400">{img.active ? 'Ativo' : 'Inativo'}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeHeroImage(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              
              {(!settingsForm.hero_images || settingsForm.hero_images.length === 0) && (
                <div className="text-center py-8 border-2 border-dashed border-stone-200 rounded-lg">
                  <ImagePlus className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">Nenhuma imagem no carrossel</p>
                  <p className="text-stone-400 text-xs">Clique em &quot;Adicionar&quot; para começar</p>
                </div>
              )}
            </TabsContent>

            {/* SEO */}
            <TabsContent value="seo" className="space-y-4">
              <div>
                <Label>Meta Título</Label>
                <Input value={settingsForm.meta_title || ''} onChange={(e) => setSettingsForm({ ...settingsForm, meta_title: e.target.value })}
                  placeholder="Mad Brothers Bakery - Artisan Cakes" className="mt-1" />
              </div>
              <div>
                <Label>Meta Descrição</Label>
                <Textarea value={settingsForm.meta_description || ''} onChange={(e) => setSettingsForm({ ...settingsForm, meta_description: e.target.value })}
                  placeholder="Descrição para motores de busca..." className="mt-1" rows={3} />
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveSettings} className="bg-[#8D6E63] hover:bg-[#8D6E63]/90">
              <Save className="w-4 h-4 mr-2" /> Guardar Tudo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
